/**
 * Their page (`/watch/:token`): the person's own page with one business (prototype/talk). Three
 * columns on a computer: the host, search and the person's places; the open place; what is pinned
 * for it. An iPad upright has two panes, a phone has its home and the screens it pushes.
 */
import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { useToast } from "../../components/ui/Toast";
import { api, ApiError } from "../../lib/api";
import type { LiveNoteUpload } from "../../lib/liveNoteUpload";
import { pageNoteApi, recordNote } from "../../lib/noteUpload";
import type { GuestPrefs, GuestTalkResponse } from "../../../shared/talk";
import type { Attachment, WatchComment, WatchPageResponse, WatchPin } from "../../../shared/types";
import { applyBrandIcon } from "../brandIcon";
import { squarePhoto } from "./photo";
import { afterLeaving } from "../series";
import { useMediaQuery } from "../smallScreen";
import { TroublePage, troubleOf } from "../TroublePage";
import { forgetToken } from "../watchToken";
import { type CallSheetData, type Chip, type Lay, type MenuState, PageContext, type PageValue, type Replying, type SheetKey, type Viewing } from "./context";
import { lookStyle } from "./look";
import { Menu, Viewer } from "./Menus";
import {
  answersOf,
  buildConversation,
  continueOf,
  defaultRoom,
  type Entry,
  filesOf,
  firstWord,
  hostOf,
  pinsOnPage,
  quickCallsOf,
} from "./model";
import { Places } from "./Places";
import { livePins } from "./pins";
import { Sheets } from "./Sheets";
import { openingPlace, type Place, parsePlace, placeExists, placeParams, seenKey } from "./talk";
import {
  addOutgoing,
  applyRead,
  emptyThread,
  markFailed,
  markSending,
  markSent,
  newClientId,
  nextSend,
  retryOutgoing,
  type ThreadState,
} from "./thread";
import { useNewsWires, useRoomWires } from "./wires";
import "../../styles/their-page.css";

const PHONE = "(max-width: 699px)";
const UPRIGHT = "(max-width: 1023px)";
const SIDE = "(max-width: 1399px)";

/** Read and write a small set this browser keeps for the page; a browser that keeps nothing starts empty. */
function useKeptSet(key: string): [(k: string) => boolean, (k: string) => void] {
  const [set, setSet] = useState<Set<string>>(() => {
    try {
      return new Set(JSON.parse(window.localStorage.getItem(key) ?? "[]") as string[]);
    } catch {
      return new Set();
    }
  });
  const has = useCallback((k: string) => set.has(k), [set]);
  const add = useCallback(
    (k: string) =>
      setSet((prev) => {
        const next = new Set(prev).add(k);
        try {
          window.localStorage.setItem(key, JSON.stringify([...next]));
        } catch {
          /* kept for this visit only */
        }
        return next;
      }),
    [key],
  );
  return [has, add];
}

/** What a send carries beside its words until it has gone: files to upload, or a recorded video. */
interface Waiting {
  files?: Chip[];
  /** A video they recorded or picked: it goes the notes way, made playable, one to a message. A picked one has its size. */
  video?: { blob: Blob; name: string; preview: string; size?: string; live?: LiveNoteUpload | null };
  attachments?: Attachment[];
}

export function TheirPage(): ReactNode {
  const { token = "" } = useParams<{ token: string }>();
  const [phase, setPhase] = useState<"loading" | "ready" | "trouble">("loading");
  const [trouble, setTrouble] = useState<ReturnType<typeof troubleOf>>({ kind: "seat" });
  const [data, setData] = useState<WatchPageResponse | null>(null);
  const [thread, setThread] = useState<ThreadState>(emptyThread);
  const [local, setLocal] = useState<Map<string, WatchComment[]>>(new Map());
  const [pins, setPins] = useState<WatchPin[]>([]);
  const [avatar, setAvatar] = useState<string | null>(null);
  const [guest, setGuest] = useState<GuestTalkResponse | null>(null);
  const reads = useRef(0);
  const applied = useRef(0);
  const hostSent = useRef(new Map<string, number>());
  const guestReads = useRef(0);
  const guestApplied = useRef(0);

  const [typingRooms, setTypingRooms] = useState<Set<string>>(new Set());
  const typingOff = useRef(new Map<string, number>());
  const stopTyping = useCallback((webinarId: string) => {
    window.clearTimeout(typingOff.current.get(webinarId));
    setTypingRooms((s) => {
      if (!s.has(webinarId)) return s;
      const next = new Set(s);
      next.delete(webinarId);
      return next;
    });
  }, []);

  // --- reading the page: every read is numbered, and one older than the read drawn is dropped ---
  const read = useCallback(async () => {
    const n = ++reads.current;
    const res = await api.watch.page(token);
    if (n < applied.current) return;
    applied.current = n;
    // Why here: once the host's line has landed the dots are done, not left for the timer.
    for (const s of res.sessions) {
      const sent = s.messages.filter((m) => m.sender === "host").length;
      const before = hostSent.current.get(s.webinar_id);
      hostSent.current.set(s.webinar_id, sent);
      if (before !== undefined && sent > before) stopTyping(s.webinar_id);
    }
    setData(res);
    setThread((t) => applyRead(t, n, res.sessions.flatMap((s) => s.messages)));
    setPins(res.pins ?? []);
    setAvatar(res.registrant_avatar_url ?? null);
    setLocal((prev) => {
      if (prev.size === 0) return prev;
      const shown = new Set(res.sessions.flatMap((s) => s.posts.flatMap((p) => p.comments.map((c) => c.id))));
      const next = new Map<string, WatchComment[]>();
      for (const [postId, list] of prev) {
        const left = list.filter((c) => !shown.has(c.id));
        if (left.length) next.set(postId, left);
      }
      return next;
    });
  }, [token, stopTyping]);

  const readGuest = useCallback(async () => {
    const n = ++guestReads.current;
    const res = await api.guestTalk.get(token);
    if (n < guestApplied.current) return;
    guestApplied.current = n;
    setGuest(res);
  }, [token]);

  useEffect(() => {
    let alive = true;
    Promise.all([read(), readGuest()])
      .then(() => alive && setPhase("ready"))
      .catch((err: unknown) => {
        if (!alive) return;
        setTrouble(troubleOf(err));
        setPhase("trouble");
      });
    return () => {
      alive = false;
    };
  }, [read, readGuest]);
  const refreshGuest = useCallback(() => {
    readGuest().catch(() => {});
  }, [readGuest]);
  const refresh = useCallback(() => {
    read().catch(() => {});
    refreshGuest();
  }, [read, refreshGuest]);

  // --- the live wires: a refresh re-reads the page, typing lights the dots ---
  const onTyping = useCallback(
    (webinarId: string) => {
      setTypingRooms((s) => new Set(s).add(webinarId));
      window.clearTimeout(typingOff.current.get(webinarId));
      typingOff.current.set(webinarId, window.setTimeout(() => stopTyping(webinarId), 6_000));
    },
    [stopTyping],
  );
  const roomIds = useMemo(() => (data?.sessions ?? []).map((s) => s.webinar_id), [data?.sessions]);
  const noteTyping = useRoomWires(token, roomIds, refresh, onTyping);
  // Why these too: a post or a line pokes its room's or circle's wire, not the private one above.
  const newsPaths = useMemo(() => {
    const t = encodeURIComponent(token);
    return (guest?.rooms ?? []).flatMap((r) => [`/api/watch/${t}/everyone/${encodeURIComponent(r.webinarId)}/ws`, ...r.circles.map((c) => `/api/watch/${t}/circles/${encodeURIComponent(c.circleId)}/ws`)]);
  }, [token, guest?.rooms]);
  useNewsWires(newsPaths, refreshGuest);

  // --- the clock and the screen ---
  const [nowMs, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, []);
  const phone = useMediaQuery(PHONE);
  const upright = useMediaQuery(UPRIGHT);
  const side = useMediaQuery(SIDE);
  const lay: Lay = phone ? "phone" : upright ? "upright" : side ? "side" : "computer";

  // --- places: the address says where the person is, and the page opens on What's new when there is news ---
  const [params, setParams] = useSearchParams();
  const [opening, setOpening] = useState<Place | null>(null);
  useEffect(() => {
    if (guest && !opening) setOpening(openingPlace(guest));
  }, [guest, opening]);
  const asked = parsePlace(params.get("at"), params.get("q"));
  const place: Place = useMemo(() => {
    const home: Place = { kind: "new" };
    if (phone) return asked && guest && placeExists(asked, guest) ? asked : home;
    if (asked && guest && placeExists(asked, guest)) return asked;
    return opening ?? home;
  }, [asked, guest, opening, phone]);
  const goPlace = useCallback(
    (p: Place, replace = false) => {
      setMenu(null);
      setSheets([]);
      setParams(placeParams(p), { replace });
    },
    [setParams],
  );
  const back = useCallback(() => setParams({}), [setParams]);
  const seen = useCallback(
    (p: Place) => {
      const key = seenKey(p);
      if (key) api.guestTalk.seen(token, key).then(readGuest).catch(() => {});
    },
    [token, readGuest],
  );
  const markAllSeen = useCallback(() => {
    api.guestTalk
      .seen(token, "all")
      .then(readGuest)
      .catch(() => {});
    // Why the person stays: What's new says they are caught up, and leaves the left list once they go elsewhere.
    setOpening({ kind: "new" });
  }, [token, readGuest]);
  const setPrefs = useCallback(
    (p: Partial<GuestPrefs>) => {
      setGuest((g) => (g ? { ...g, prefs: { ...g.prefs, ...p } } : g));
      api.guestTalk.prefs(token, p).catch(() => readGuest().catch(() => {}));
    },
    [token, readGuest],
  );
  const [roomOpen, setRoomOpen] = useState<string | null>(null);

  // --- the page's own state ---
  const [pinsAll, setPinsAll] = useState(false);
  const [openThreads, setOpenThreads] = useState<Set<string>>(new Set());
  const toggleThread = useCallback((key: string, open?: boolean) => {
    setOpenThreads((s) => {
      const next = new Set(s);
      if (open ?? !s.has(key)) next.add(key);
      else next.delete(key);
      return next;
    });
  }, []);
  const [sheets, setSheets] = useState<SheetKey[]>([]);
  const openSheet = useCallback((k: SheetKey, push = false) => {
    setMenu(null);
    setSheets((s) => (push ? [...s, k] : [k]));
  }, []);
  const closeSheet = useCallback(() => setSheets([]), []);
  const openRoom = useCallback(
    (webinarId: string) => {
      setRoomOpen(webinarId);
      openSheet("room");
    },
    [openSheet],
  );
  const [call, setCall] = useState<CallSheetData | null>(null);
  const openCall = useCallback(
    (c: CallSheetData) => {
      setCall(c);
      openSheet("call");
    },
    [openSheet],
  );
  const backSheet = useCallback(() => setSheets((s) => s.slice(0, -1)), []);
  const [menu, setMenu] = useState<MenuState | null>(null);
  // The app's one toast, as every screen shows its toasts.
  const showToast = useToast();
  const toast = useCallback((words: string) => showToast(words), [showToast]);
  const [playing, setPlaying] = useState<PageValue["playing"]>(null);
  const [captionsOn, setCaptionsOn] = useState(true);
  const toggleCaptions = useCallback(() => setCaptionsOn((on) => !on), []);
  const [inCalendar, markCalendar] = useKeptSet(`tp.cal.${token}`);
  const [saved, markSaved] = useKeptSet(`tp.saved.${token}`);
  const [replying, setReplying] = useState<Replying | null>(null);
  const [viewing, view] = useState<Viewing | null>(null);

  // --- sending: one at a time, in order, each with its own page id (specs/formal/living-thread) ---
  const waiting = useRef(new Map<string, Waiting>());
  const inflight = useRef(false);
  const [pump, setPump] = useState(0);
  useEffect(() => {
    if (inflight.current) return;
    const next = nextSend(thread);
    if (!next) return;
    const { outgoing: o, reply_to } = next;
    inflight.current = true;
    setThread((t) => markSending(t, o.cid));
    (async () => {
      const w = waiting.current.get(o.cid) ?? {};
      let attachments = w.attachments;
      if (!attachments) {
        attachments = [];
        for (const chip of w.files ?? []) {
          const asset = await api.watch.upload(token, chip.file);
          attachments.push({ kind: asset.kind === "image" ? "image" : "file", name: asset.name, size: asset.size, key: asset.key });
        }
        if (w.video) {
          const note = await recordNote("video", w.video.blob, { notes: pageNoteApi(token), name: w.video.name, live: w.video.live });
          attachments.push(w.video.size ? { ...note.attachment, name: w.video.name, size: w.video.size } : note.attachment);
        }
        waiting.current.set(o.cid, { ...w, attachments });
      }
      const sent = await api.watch.message(token, {
        webinar_id: o.webinar_id,
        body: o.body,
        client_id: o.cid,
        ...(reply_to ? { reply_to } : {}),
        ...(o.answer_to ? { answer_to: o.answer_to } : {}),
        ...(o.reply_post ? { reply_post: o.reply_post.id } : {}),
        ...(attachments.length ? { attachments } : {}),
      });
      setThread((t) => markSent(t, o.cid, sent));
      if (o.answer_to) refresh();
    })()
      .catch((err: unknown) => {
        setThread((t) => markFailed(t, o.cid));
        if (err instanceof ApiError && err.status >= 400 && err.status < 500) toast(err.message);
      })
      .finally(() => {
        inflight.current = false;
        setPump((p) => p + 1);
      });
  }, [thread, token, toast, pump, refresh]);

  const host = useMemo(() => (data ? hostOf(data) : { name: "", first: "", avatar: null }), [data]);
  const nowSec = Math.floor(nowMs / 1000);
  // Why these are left out: a room is on the left side, and a circle's call is in its circle.
  const notCalls = useMemo(() => {
    const ids = new Set((guest?.rooms ?? []).map((r) => r.webinarId));
    if (guest?.liveNow?.circleId) ids.add(guest.liveNow.webinarId);
    return ids;
  }, [guest]);
  const calls = useMemo(() => quickCallsOf(data?.sessions ?? [], notCalls), [data?.sessions, notCalls]);
  const convo = useMemo(() => buildConversation(data ?? { sessions: [] }, thread, local, Math.floor(Date.now() / 1000), calls), [data, thread, local, calls]);
  const files = useMemo(() => filesOf(data ?? { sessions: [] }, convo, Math.floor(Date.now() / 1000)), [data, convo]);
  const answers = useMemo(() => answersOf(data?.answers ?? []), [data?.answers]);
  const livePinned = useMemo(() => pinsOnPage(livePins(pins, nowSec), convo, files), [pins, nowSec, convo, files]);
  const toRoom = useMemo(() => defaultRoom(data?.sessions ?? []), [data?.sessions]);

  const sendText = useCallback(
    (body: string, chips: Chip[]) => {
      const r = replying;
      if (r?.kind === "post") {
        (async () => {
          const attachments: Attachment[] = [];
          for (const chip of chips) {
            const asset = await api.watch.upload(token, chip.file);
            attachments.push({ kind: asset.kind === "image" ? "image" : "file", name: asset.name, size: asset.size, key: asset.key });
          }
          const c = await api.watch.comment(token, r.postId, { body, ...(attachments.length ? { attachments } : {}) });
          setLocal((prev) => new Map(prev).set(r.postId, [...(prev.get(r.postId) ?? []), c]));
          toggleThread(r.entryKey, true);
        })().catch((err: unknown) => toast(err instanceof ApiError ? err.message : "That did not send. Try again."));
        setReplying(null);
        return;
      }
      const webinarId = r?.webinar_id ?? toRoom;
      const reply = r?.kind === "message" ? { reply: r.target } : r?.kind === "announcement" ? { reply_post: r.post } : {};
      // Each video goes on its own, the notes way; pictures and files ride with the words.
      const videos = chips.filter((c) => c.kind === "video");
      const rest = chips.filter((c) => c.kind !== "video");
      setThread((t) => {
        let next = t;
        for (const v of videos) {
          const cid = newClientId();
          waiting.current.set(cid, { video: { blob: v.file, name: v.name, preview: v.preview ?? URL.createObjectURL(v.file), size: v.size } });
          next = addOutgoing(next, { cid, webinar_id: webinarId, body: "", ...reply });
        }
        if (body || rest.length) {
          const cid = newClientId();
          if (rest.length) waiting.current.set(cid, { files: rest });
          next = addOutgoing(next, { cid, webinar_id: webinarId, body, ...reply });
        }
        return next;
      });
      if (r?.kind === "message") toggleThread(r.entryKey, true);
      setReplying(null);
    },
    [replying, token, toRoom, toast, toggleThread],
  );

  const sendVideo = useCallback(
    (blob: Blob, live: LiveNoteUpload | null) => {
      const r = replying?.kind === "message" ? replying : null;
      const cid = newClientId();
      waiting.current.set(cid, { video: { blob, name: "Video note", preview: URL.createObjectURL(blob), live } });
      setThread((t) => addOutgoing(t, { cid, webinar_id: r?.webinar_id ?? toRoom, body: "", ...(r ? { reply: r.target } : {}) }));
      if (r) toggleThread(r.entryKey, true);
      setReplying(null);
    },
    [replying, toRoom, toggleThread],
  );

  const retry = useCallback((cid: string) => setThread((t) => retryOutgoing(t, cid)), []);
  const localMedia = useCallback((cid: string) => waiting.current.get(cid)?.video?.preview ?? null, []);
  const localPicked = useCallback((cid: string) => {
    const v = waiting.current.get(cid)?.video;
    return v?.size ? { name: v.name, size: v.size } : null;
  }, []);
  const localFiles = useCallback((cid: string) => waiting.current.get(cid)?.files ?? [], []);

  const replyTo = useCallback(
    (e: Entry) => {
      const root = e.parent ? convo.byId.get(e.parent) ?? e : e;
      if (root.kind === "post") setReplying({ kind: "post", postId: root.id, entryKey: root.key, webinar_id: root.webinar_id });
      else {
        const cid = root.message?.client_id ?? root.outgoing?.cid;
        const target = cid && root.from === "me" ? { cid } : { id: root.id };
        setReplying({ kind: "message", target, entryKey: root.key, webinar_id: root.webinar_id });
      }
    },
    [convo],
  );

  const patchData = useCallback((f: (d: WatchPageResponse) => WatchPageResponse) => setData((d) => (d ? f(d) : d)), []);

  const love = useCallback(
    (e: Entry) => {
      if (e.post) {
        const was = e.post.liked;
        const set = (liked: boolean) =>
          patchData((d) => ({ ...d, sessions: d.sessions.map((s) => ({ ...s, posts: s.posts.map((p) => (p.id === e.id ? { ...p, liked } : p)) })) }));
        set(!was);
        api.watch.react(token, e.id).then((r) => set(r.liked)).catch(() => set(was));
        return;
      }
      if (e.kind !== "message" || e.from !== "host") return;
      const was = Boolean(e.message?.liked);
      const set = (liked: boolean) =>
        setThread((t) => ({ ...t, server: t.server.map((m) => (m.id === e.id ? { ...m, liked: liked ? 1 : undefined } : m)) }));
      set(!was);
      api.watch.love(token, e.id).then((r) => set(r.liked)).catch(() => set(was));
    },
    [token, patchData],
  );

  const pinOf = useCallback((e: Entry) => livePinned.find((p) => p.target_id === e.id), [livePinned]);
  const isPinned = useCallback((e: Entry) => Boolean(pinOf(e)), [pinOf]);
  const unpin = useCallback(
    (pin: WatchPin) => {
      setPins((list) => list.filter((p) => p.id !== pin.id));
      toast(pin.by === "host" ? "Unpinned for you." : "Unpinned.");
      api.watch.unpin(token, pin.id).catch(() => refresh());
    },
    [token, toast, refresh],
  );
  const togglePin = useCallback(
    (e: Entry) => {
      const on = pinOf(e);
      if (on) {
        unpin(on);
        return;
      }
      if (e.outgoing && !e.outgoing.sent) return;
      const kind = e.kind === "comment" ? "comment" : e.kind;
      api.watch
        .pin(token, { webinar_id: e.webinar_id, target_kind: kind, target_id: e.id })
        .then((p) => {
          setPins((list) => [p, ...list.filter((x) => x.id !== p.id)]);
          toast("Pinned.");
        })
        .catch((err: unknown) => toast(err instanceof ApiError ? err.message : "That did not pin. Try again."));
    },
    [pinOf, unpin, token, toast],
  );
  const seePin = useCallback(
    (pin: WatchPin) => {
      if (pin.seen) return;
      setPins((list) => list.map((p) => (p.id === pin.id ? { ...p, seen: 1 } : p)));
      api.watch.seePin(token, pin.id).catch(() => {});
    },
    [token],
  );

  const goTo = useCallback(
    (entryId: string) => {
      setSheets([]);
      const e = convo.byId.get(entryId);
      if (!e) return;
      if (e.parent) {
        const parent = convo.byId.get(e.parent);
        if (parent) toggleThread(parent.key, true);
      }
      window.requestAnimationFrame(() =>
        window.requestAnimationFrame(() => {
          const row = document.querySelector<HTMLElement>(`.tp .tin [data-key="${CSS.escape(e.key)}"]`);
          if (!row) return;
          row.scrollIntoView({ block: "center", behavior: "smooth" });
          window.setTimeout(
            () => row.querySelector(".mw")?.animate([{ transform: "scale(1.04)" }, { transform: "none" }], { duration: 520, easing: "cubic-bezier(.3,1.3,.5,1)" }),
            380,
          );
        }),
      );
    },
    [convo, toggleThread],
  );

  const answerQuestion = useCallback(
    (questionId: string, webinarId: string, option: string) => {
      const cid = newClientId();
      setThread((t) => addOutgoing(t, { cid, webinar_id: webinarId, body: option, reply: { id: questionId }, answer_to: questionId }));
      // The Answers tab says it at once; the next read brings the server's own.
      patchData((d) => ({ ...d, answers: d.answers?.map((a) => (a.source === "question" && a.ref === questionId ? { ...a, answer: option } : a)) }));
    },
    [patchData],
  );

  const changeAnswer = useCallback(
    async (a: PageValue["answers"][number], value: string) => {
      if (a.source === "signup") await api.watch.answer(token, { webinar_id: a.webinar_id, key: a.ref, value });
      else if (a.source === "moment") {
        const moment = data?.sessions.flatMap((s) => s.interactions).find((i) => i.id === a.ref);
        await api.watch.respond(token, {
          interaction_id: a.ref,
          value: moment?.type === "question" ? { kind: "question", text: value } : { kind: "poll", option: value },
        });
      } else if (a.answer_id) await api.watch.editMessage(token, a.answer_id, { body: value });
      else {
        answerQuestion(a.ref, a.webinar_id, value);
        return;
      }
      toast(`Saved. ${host.first || "They"} sees your new answer.`);
      refresh();
    },
    [token, data, answerQuestion, toast, refresh, host.first],
  );

  const leaveSeries = useCallback(
    async (webinarId: string) => {
      await api.watch.leaveSeries(token, { webinar_id: webinarId });
      patchData((d) => ({
        ...d,
        sessions: d.sessions.map((s) =>
          s.webinar_id === webinarId && s.live?.series ? { ...s, live: { ...s.live, series: afterLeaving(s.live.series) } } : s,
        ),
      }));
      toast(`You left ${data?.sessions.find((s) => s.webinar_id === webinarId)?.title ?? "the series"}.`);
    },
    [token, patchData, toast, data?.sessions],
  );

  const setPhoto = useCallback(
    async (file: File | null) => {
      if (!file) {
        const res = await api.watch.clearPhoto(token);
        setAvatar(res.avatar_url);
        toast("Photo removed.");
        return;
      }
      const res = await api.watch.setPhoto(token, await squarePhoto(file));
      setAvatar(res.avatar_url);
      toast("Photo updated.");
    },
    [token, toast],
  );

  const notYou = useCallback(() => {
    forgetToken(token);
    const slug = data?.sessions[0]?.slug;
    window.location.assign(slug ? `/w/${encodeURIComponent(slug)}` : "/");
  }, [token, data?.sessions]);

  const playTalk = useCallback(
    (webinarId: string, opts: { sessionId?: string; start?: boolean; at?: number } = {}) => {
      setPlaying({
        webinar_id: webinarId,
        ...(opts.sessionId ? { session_id: opts.sessionId } : {}),
        ...(opts.start ? { start: true } : {}),
        ...(opts.at !== undefined ? { at: opts.at } : {}),
      });
      openSheet("talk", sheets[0] === "room");
    },
    [openSheet, sheets],
  );

  // The tab wears the business's icon, and the address bar its paper.
  useEffect(() => {
    if (!data) return;
    applyBrandIcon(data.brand);
    document.title = `${host.name} · ${data.workspace_name}`;
  }, [data, host.name]);

  if (phase === "loading") return <div className="tp tp-loading" aria-label="Loading" />;
  if (phase === "trouble" || !data || !guest) return <TroublePage kind={trouble.kind} values={{ origin: trouble.origin }} />;

  // Why the wire alone: a read's typing stamp would keep the dots on until the next read.
  const anyTyping = typingRooms.size > 0;
  const value: PageValue = {
    token,
    data,
    guest,
    host,
    me: { name: data.registrant_name, first: firstWord(data.registrant_name), email: data.registrant_email, avatar },
    online: data.host_online === 1,
    nowMs,
    convo,
    pins: livePinned,
    files,
    answers,
    resume: continueOf(data.sessions),
    typing: anyTyping,
    lay,
    phone,
    place,
    goPlace,
    back,
    seen,
    markAllSeen,
    setPrefs,
    roomOpen,
    openRoom,
    call,
    openCall,
    pinsAll,
    togglePinsAll: () => setPinsAll((v) => !v),
    openThreads,
    toggleThread,
    sheets,
    openSheet,
    closeSheet,
    backSheet,
    menu,
    openMenu: setMenu,
    toast,
    viewing,
    view,
    playing,
    playTalk,
    captionsOn,
    toggleCaptions,
    inCalendar,
    markCalendar,
    saved,
    markSaved,
    replying,
    setReplying,
    replyTo,
    sendText,
    sendVideo,
    retry,
    localMedia,
    localPicked,
    localFiles,
    love,
    isPinned,
    togglePin,
    unpin,
    seePin,
    goTo,
    answerQuestion,
    changeAnswer,
    noteTyping,
    defaultRoom: toRoom,
    leaveSeries,
    setPhoto,
    notYou,
    refresh,
    refreshGuest,
  };
  return (
    <PageContext.Provider value={value}>
      <div className="tp" data-lay={lay} style={lookStyle(data.brand)}>
        <Places />
        <Sheets />
        <Menu />
        <Viewer />
      </div>
    </PageContext.Provider>
  );
}
